import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Cube, 
  Plus,
  MagnifyingGlass,
  Pencil,
  Trash,
  Check,
  X,
  Upload,
  DownloadSimple,
  ListDashes
} from '@phosphor-icons/react';
import SortableTable from '../components/SortableTable';
import axios from 'axios';
import { toast } from 'sonner';
import { useSellerMode } from '../contexts/SellerModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductsPage() {
  const { activeSeller } = useSellerMode();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    qc_status: '',
    is_active: '',
    search: ''
  });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkCsvText, setBulkCsvText] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [variantProduct, setVariantProduct] = useState(null);
  const [variants, setVariants] = useState([]);
  const [variantForm, setVariantForm] = useState({ sku: '', size: '', color: '', material: '', price: '', mrp: '', stock: '' });

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      fetchProducts();
    }, 100);
    axios.get(`${API_URL}/api/categories`, { withCredentials: true }).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/brands`, { withCredentials: true }).then(r => setBrandsList(r.data)).catch(() => {});
    return () => clearTimeout(timer);
  }, [activeSeller?.user_id]);

  useEffect(() => { 
    const timer = setTimeout(() => fetchProducts(), 100);
    return () => clearTimeout(timer);
  }, [filters.category, filters.qc_status, filters.is_active, activeSeller?.user_id]);

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category) params.append('category', filters.category);
      if (filters.qc_status) params.append('qc_status', filters.qc_status);
      if (filters.is_active !== '') params.append('is_active', filters.is_active);
      
      const response = await axios.get(`${API_URL}/api/products?${params}`, { withCredentials: true });
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted successfully');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const handleQcAction = async (productId, status) => {
    try {
      await axios.post(`${API_URL}/api/products/${productId}/qc`, { qc_status: status }, { withCredentials: true });
      toast.success(`Product ${status}`);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update QC status');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkCsvText.trim()) {
      toast.error('Please paste CSV data');
      return;
    }
    
    const lines = bulkCsvText.trim().split('\n');
    if (lines.length < 2) {
      toast.error('CSV must have a header row and at least one data row');
      return;
    }
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const products = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const product = {};
      headers.forEach((h, idx) => {
        product[h] = values[idx] || '';
      });
      products.push(product);
    }
    
    setBulkUploading(true);
    try {
      const response = await axios.post(`${API_URL}/api/products/bulk-upload`, { products }, { withCredentials: true });
      const { created, errors, total } = response.data;
      
      if (created > 0) {
        toast.success(`${created} of ${total} products uploaded successfully`);
        fetchProducts();
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} products had errors`);
      }
      if (created === total) {
        setBulkDialogOpen(false);
        setBulkCsvText('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/export`, { withCredentials: true });
      const products = response.data.products;
      
      if (products.length === 0) {
        toast.error('No products to export');
        return;
      }
      
      const headers = ['sku', 'name', 'description', 'category', 'brand', 'price', 'mrp', 'weight', 'qc_status', 'is_active'];
      const csv = [
        headers.join(','),
        ...products.map(p => headers.map(h => `"${(p[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Products exported');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const filteredProducts = products.filter(product => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      return (
        product.sku?.toLowerCase().includes(search) ||
        product.name?.toLowerCase().includes(search) ||
        product.brand?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Variant handlers
  const openVariants = async (product) => {
    setVariantProduct(product);
    setVariantDialogOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/products/${product.product_id}/variants`, { withCredentials: true });
      setVariants(res.data);
    } catch { setVariants([]); }
  };

  const handleAddVariant = async () => {
    if (!variantProduct) return;
    try {
      const attributes = {};
      if (variantForm.size) attributes.size = variantForm.size;
      if (variantForm.color) attributes.color = variantForm.color;
      if (variantForm.material) attributes.material = variantForm.material;
      await axios.post(`${API_URL}/api/products/${variantProduct.product_id}/variants`, {
        sku: variantForm.sku || undefined,
        attributes,
        price: parseFloat(variantForm.price) || variantProduct.price,
        mrp: parseFloat(variantForm.mrp) || variantProduct.mrp,
        stock: parseInt(variantForm.stock) || 0,
      }, { withCredentials: true });
      toast.success('Variant added');
      setVariantForm({ sku: '', size: '', color: '', material: '', price: '', mrp: '', stock: '' });
      openVariants(variantProduct);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!variantProduct || !window.confirm('Delete this variant?')) return;
    try {
      await axios.delete(`${API_URL}/api/products/${variantProduct.product_id}/variants/${variantId}`, { withCredentials: true });
      toast.success('Variant deleted');
      openVariants(variantProduct);
    } catch { toast.error('Failed'); }
  };

  const getQcStatusColor = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'danger';
    return 'warning';
  };

  const productColumns = [
    { key: 'sku', label: 'SKU', render: (v, row) => <button onClick={() => navigate(`/products/${row.product_id}`)} className="font-mono text-sm font-medium text-[#7d64ff] hover:underline cursor-pointer" data-testid={`sku-link-${row.product_id}`}>{v}</button> },
    { key: 'name', label: 'Name', render: (v, row) => (<div><p className="font-medium text-[#0A0A0A]">{v}</p>{row.brand && <p className="text-xs text-[#5a5c87]">{row.brand}</p>}</div>) },
    { key: 'category', label: 'Category' },
    { key: 'hsn_code', label: 'HSN', render: v => v ? <span className="font-mono text-sm text-[#3c3c64]">{v}</span> : <span className="text-[#9c9db7] text-xs">—</span> },
    { key: 'price', label: 'Price', render: v => formatCurrency(v) },
    { key: 'mrp', label: 'MRP', render: v => <span className="text-[#5a5c87]">{formatCurrency(v)}</span> },
    { key: 'qc_status', label: 'QC', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded sp-badge-${getQcStatusColor(v)}`}>{v}</span> },
    { key: 'is_active', label: 'Status', render: v => <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${v ? 'sp-badge-success' : 'sp-badge-neutral'}`}>{v ? 'Active' : 'Inactive'}</span> },
    { key: 'product_id', label: 'Actions', sortable: false, render: (v, row) => (
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/products/${v}`)} data-testid={`edit-product-${v}`}><Pencil size={16} /></Button>
        <Button variant="ghost" size="sm" onClick={() => openVariants(row)} data-testid={`variants-product-${v}`}><ListDashes size={16} /></Button>
        {row.qc_status === 'pending' && (
          <>
            <Button variant="ghost" size="sm" className="text-[#28cd8c]" onClick={() => handleQcAction(v, 'approved')} data-testid={`approve-product-${v}`}><Check size={16} /></Button>
            <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleQcAction(v, 'rejected')} data-testid={`reject-product-${v}`}><X size={16} /></Button>
          </>
        )}
        <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleDelete(v)} data-testid={`delete-product-${v}`}><Trash size={16} /></Button>
      </div>
    )}
  ];

  const l1Cats = categories.filter(c => c.level === 1);

  return (
    <DashboardLayout 
      title="Products" 
      subtitle="Manage your product catalog"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv} className="border-[#e5e5ea]" data-testid="export-products-btn">
            <DownloadSimple size={18} className="mr-2" /> Export
          </Button>
          <Button variant="outline" onClick={() => setBulkDialogOpen(true)} className="border-[#e5e5ea]" data-testid="bulk-upload-btn">
            <Upload size={18} className="mr-2" /> Bulk Upload
          </Button>
          <Button onClick={() => navigate('/products/new')} className="bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="add-product-btn">
            <Plus size={18} className="mr-2" /> Add Product
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <div className="bg-white border border-[#e5e5ea] rounded-xl mb-5">
        <div className="action-bar">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" size={16} />
            <Input placeholder="Search by SKU, Name, or Brand..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} className="pl-9 h-9 text-sm" data-testid="product-search" />
          </div>
          <Select value={filters.category || "all"} onValueChange={(value) => setFilters({ ...filters, category: value === "all" ? "" : value })}>
            <SelectTrigger className="w-40 h-9" data-testid="category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {l1Cats.map(cat => <SelectItem key={cat.category_id} value={cat.name}>{cat.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.qc_status || "all"} onValueChange={(value) => setFilters({ ...filters, qc_status: value === "all" ? "" : value })}>
            <SelectTrigger className="w-32 h-9" data-testid="qc-filter"><SelectValue placeholder="QC Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All QC</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : (
        <SortableTable columns={productColumns} data={filteredProducts} pageSize={15} emptyMessage="No products found" emptyIcon={Cube} />
      )}

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-bold">Bulk Product Upload</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#fcfcfc] border border-[#e5e5ea] rounded text-sm">
              <p className="font-semibold mb-2">CSV Format (comma-separated):</p>
              <code className="text-xs block bg-white p-2 rounded border border-[#e5e5ea] font-mono">
                sku,name,description,category,brand,price,mrp,weight<br/>
                SKU001,Product Name,Description,Electronics,Brand,999,1299,0.5
              </code>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Paste CSV Data</Label>
              <Textarea
                value={bulkCsvText}
                onChange={e => setBulkCsvText(e.target.value)}
                rows={10}
                placeholder="sku,name,description,category,brand,price,mrp,weight&#10;SKU001,Widget A,A great widget,Electronics,BrandX,499,699,0.3"
                className="mt-1 rounded-sm font-mono text-xs"
                data-testid="bulk-csv-input"
              />
            </div>
            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setBulkDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button 
                onClick={handleBulkUpload} 
                disabled={bulkUploading} 
                className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                data-testid="bulk-upload-submit"
              >
                {bulkUploading ? 'Uploading...' : 'Upload Products'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Variants Dialog */}
      <Dialog open={variantDialogOpen} onOpenChange={setVariantDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Variants: {variantProduct?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {variants.length > 0 ? (
              <div className="border border-[#e5e5ea] rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-[#e5e5ea] bg-[#fcfcfc]">
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-[#5a5c87]">SKU</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-[#5a5c87]">Attributes</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-[#5a5c87]">Price</th>
                    <th className="text-left p-3 text-xs font-bold uppercase tracking-wider text-[#5a5c87]">Stock</th>
                    <th className="p-3"></th>
                  </tr></thead>
                  <tbody>
                    {variants.map(v => (
                      <tr key={v.variant_id} className="border-b border-[#f0f0f0] hover:bg-[#fafafe]">
                        <td className="p-3 font-mono text-xs">{v.sku}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(v.attributes || {}).map(([k, val]) => (
                              <span key={k} className="text-[10px] px-1.5 py-0.5 bg-[#f7f7fa] rounded border border-[#e5e5ea]">{k}: {val}</span>
                            ))}
                          </div>
                        </td>
                        <td className="p-3 font-medium">{formatCurrency(v.price)}</td>
                        <td className="p-3">{v.stock}</td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm" className="text-[#ea4b5a]" onClick={() => handleDeleteVariant(v.variant_id)} data-testid={`delete-variant-${v.variant_id}`}><Trash size={14} /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-6 text-center text-[#9c9db7] text-sm">No variants yet</div>
            )}

            {/* Add Variant Form */}
            <div className="border-t border-[#e5e5ea] pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-[#5a5c87] mb-3">Add Variant</p>
              <div className="grid grid-cols-3 gap-3">
                <Input placeholder="Size (e.g. S, M, L)" value={variantForm.size} onChange={e => setVariantForm({ ...variantForm, size: e.target.value })} className="rounded-sm text-sm" data-testid="variant-size" />
                <Input placeholder="Color" value={variantForm.color} onChange={e => setVariantForm({ ...variantForm, color: e.target.value })} className="rounded-sm text-sm" data-testid="variant-color" />
                <Input placeholder="Material" value={variantForm.material} onChange={e => setVariantForm({ ...variantForm, material: e.target.value })} className="rounded-sm text-sm" data-testid="variant-material" />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-3">
                <Input placeholder="SKU (auto)" value={variantForm.sku} onChange={e => setVariantForm({ ...variantForm, sku: e.target.value })} className="rounded-sm text-sm" />
                <Input type="number" placeholder="Price" value={variantForm.price} onChange={e => setVariantForm({ ...variantForm, price: e.target.value })} className="rounded-sm text-sm" data-testid="variant-price" />
                <Input type="number" placeholder="MRP" value={variantForm.mrp} onChange={e => setVariantForm({ ...variantForm, mrp: e.target.value })} className="rounded-sm text-sm" />
                <Input type="number" placeholder="Stock" value={variantForm.stock} onChange={e => setVariantForm({ ...variantForm, stock: e.target.value })} className="rounded-sm text-sm" data-testid="variant-stock" />
              </div>
              <Button onClick={handleAddVariant} className="mt-3 bg-[#7d64ff] hover:bg-[#6153a7] text-white" disabled={!variantForm.size && !variantForm.color && !variantForm.material} data-testid="add-variant-btn">
                <Plus size={14} className="mr-1" /> Add Variant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
